import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Image,
  Input,
  Row,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  CustomerServiceOutlined,
  MessageOutlined,
  PaperClipOutlined,
  ReloadOutlined,
  SendOutlined,
  UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getHelpTicket,
  replyHelpTicket,
  updateHelpTicketStatus,
} from '../../api/helpCenter';
import { allowedTicketStatuses } from './helpCenterWorkflow';
import './ticketDetail.css';

const { Paragraph, Text, Title } = Typography;

const statusMeta = {
  OPEN: { label: '待处理', color: 'orange' },
  IN_PROGRESS: { label: '处理中', color: 'blue' },
  WAITING_USER: { label: '等待用户', color: 'purple' },
  RESOLVED: { label: '已解决', color: 'green' },
  CLOSED: { label: '已关闭', color: 'default' },
};

const categoryLabels = {
  PRODUCT_SEARCH: '商品搜索与选品',
  REBATE_ORDER: '返利订单',
  WALLET_WITHDRAWAL: '钱包与提现',
  MEMBERSHIP: '会员与权益',
  AI_GUIDE: 'AI 导购',
  PRICE_MONITOR: '降价提醒',
  MESSAGE_CENTER: '消息中心',
  INVITE: '邀请奖励',
  ACCOUNT_SECURITY: '账号安全',
};

const formatDate = (value) => value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-';

const TicketDetail = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reply, setReply] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [saving, setSaving] = useState(false);

  const loadTicket = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setTicket(await getHelpTicket(ticketId));
    } catch (requestError) {
      setError(requestError?.message || '反馈详情加载失败');
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => { loadTicket(); }, [loadTicket]);

  const updateStatus = async (status) => {
    if (!ticket || status === ticket.status) return;
    setSaving(true);
    try {
      await updateHelpTicketStatus(ticket.id, {
        status,
        note: statusNote.trim() || undefined,
        version: ticket.version,
      });
      message.success('工单状态已更新');
      setStatusNote('');
      await loadTicket();
    } catch (requestError) {
      message.error(requestError?.message || '工单状态更新失败');
    } finally {
      setSaving(false);
    }
  };

  const sendReply = async () => {
    const body = reply.trim();
    if (!ticket || !body || body.length > 150) return;
    setSaving(true);
    try {
      await replyHelpTicket(ticket.id, { body });
      setReply('');
      message.success('回复已发送');
      await loadTicket();
    } catch (requestError) {
      message.error(requestError?.message || '回复发送失败');
    } finally {
      setSaving(false);
    }
  };

  const initialAttachments = ticket?.messages?.[0]?.attachments || [];
  const conversationMessages = ticket?.messages?.filter((item, index) => !(
    index === 0
    && item.senderType === 'USER'
    && item.body?.trim() === ticket.description?.trim()
  )) || [];

  return (
    <div className="ticket-detail-page">
      <div className="ticket-detail-header">
        <div className="ticket-header-actions">
          <Button className="ticket-back-button" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>返回列表</Button>
          <Button className="ticket-refresh-button" icon={<ReloadOutlined />} onClick={loadTicket} loading={loading}>刷新</Button>
        </div>
      </div>

      {loading && !ticket ? <Card><Skeleton active paragraph={{ rows: 8 }} /></Card> : null}
      {error && !ticket ? (
        <Alert
          type="error"
          showIcon
          message="无法加载反馈详情"
          description={error}
          action={<Button onClick={loadTicket}>重试</Button>}
        />
      ) : null}

      {ticket ? (
        <>
          <Row gutter={[16, 16]} className="ticket-summary-row">
            <Col xs={24} xl={16}>
              <Card className="ticket-issue-panel" bordered={false}>
                <div className="ticket-issue-heading">
                  <div className="ticket-issue-badges">
                    <Text type="secondary">用户反馈内容 · 工单 #{ticket.id}</Text>
                    <Tag className="ticket-status-tag" color={statusMeta[ticket.status]?.color}>{statusMeta[ticket.status]?.label || ticket.status}</Tag>
                    <Tag className="ticket-category-tag">{categoryLabels[ticket.categoryCode] || ticket.categoryCode}</Tag>
                  </div>
                  <Title level={3}>{ticket.title}</Title>
                </div>
                <div className="ticket-description">
                  <Text className="ticket-description-label" type="secondary">问题描述</Text>
                  <Paragraph>{ticket.description}</Paragraph>
                  {initialAttachments.length > 0 && (
                    <div className="ticket-attachment-block">
                      <Text type="secondary"><PaperClipOutlined /> 问题附件（{initialAttachments.length}）</Text>
                      <Image.PreviewGroup>
                        <div className="ticket-attachment-grid">
                          {initialAttachments.map((attachment) => (
                            <Image key={attachment.id} width={72} height={72} src={attachment.url} alt={attachment.filename || '反馈截图'} />
                          ))}
                        </div>
                      </Image.PreviewGroup>
                    </div>
                  )}
                </div>
                <div className="ticket-meta-grid">
                  <div><Text type="secondary">用户 ID</Text><Text strong>{ticket.userId}</Text></div>
                  <div><Text type="secondary">创建时间</Text><Text strong>{formatDate(ticket.createdAt)}</Text></div>
                  <div><Text type="secondary">最近更新</Text><Text strong>{formatDate(ticket.updatedAt)}</Text></div>
                  <div><Text type="secondary">来源页面</Text><Text strong>{ticket.sourceRoute || '-'}</Text></div>
                  <div><Text type="secondary">应用版本</Text><Text strong>{ticket.appVersion || '-'}</Text></div>
                  <div><Text type="secondary">Android</Text><Text strong>{ticket.androidVersion || '-'}</Text></div>
                </div>
              </Card>
            </Col>
            <Col xs={24} xl={8}>
              <Card
                title={<span className="ticket-card-title"><CustomerServiceOutlined /> 处理工单</span>}
                className="ticket-action-panel"
                bordered={false}
              >
                <Space direction="vertical" size={10} style={{ width: '100%' }}>
                  <div>
                    <Text type="secondary">工单状态</Text>
                    <Select
                      value={ticket.status}
                      onChange={updateStatus}
                      loading={saving}
                      style={{ width: '100%', marginTop: 6 }}
                      options={allowedTicketStatuses(ticket.status).map((value) => ({ value, label: statusMeta[value]?.label || value }))}
                    />
                  </div>
                  <div>
                    <Text type="secondary">处理备注</Text>
                    <Input.TextArea
                      value={statusNote}
                      onChange={(event) => setStatusNote(event.target.value.slice(0, 500))}
                      rows={2}
                      maxLength={500}
                      showCount
                      placeholder="状态变更原因或处理说明（可选）"
                      style={{ marginTop: 6 }}
                    />
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>

          <section className="ticket-conversation-panel">
            <div className="ticket-section-heading">
              <div>
                <div className="ticket-panel-eyebrow">沟通记录</div>
                <Title level={4}><MessageOutlined /> 对话时间线</Title>
              </div>
              <span className="ticket-message-count">{conversationMessages.length} 条消息</span>
            </div>
            <div className="ticket-message-list">
              {conversationMessages.length ? conversationMessages.map((item) => {
                const isAdmin = item.senderType === 'ADMIN';
                return (
                  <div key={item.id} className={`ticket-message ${isAdmin ? 'admin' : 'user'}`}>
                    <div className="ticket-message-avatar">{isAdmin ? <CustomerServiceOutlined /> : <UserOutlined />}</div>
                    <div className="ticket-message-content">
                      <Space className="ticket-message-meta">
                        <Text strong>{isAdmin ? '客服回复' : '用户补充'}</Text>
                        <Text type="secondary">{formatDate(item.createdAt)}</Text>
                      </Space>
                      <Paragraph>{item.body}</Paragraph>
                      {item.attachments?.length > 0 && (
                        <Image.PreviewGroup>
                          <div className="ticket-attachment-grid compact">
                            {item.attachments.map((attachment) => <Image key={attachment.id} width={96} height={96} src={attachment.url} alt={attachment.filename || '工单截图'} />)}
                          </div>
                        </Image.PreviewGroup>
                      )}
                    </div>
                  </div>
                );
              }) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无追加对话" />}
            </div>

            {ticket.status !== 'CLOSED' ? (
              <div className="ticket-reply-composer">
                <div className="ticket-reply-label"><Text strong>回复用户</Text><Text type="secondary">内容会同步到用户端反馈详情</Text></div>
                <div className="ticket-reply-bar">
                  <Input.TextArea
                    value={reply}
                    onChange={(event) => setReply(event.target.value.slice(0, 150))}
                    maxLength={150}
                    showCount
                    autoSize={{ minRows: 2, maxRows: 4 }}
                    placeholder="请输入回复内容，最多 150 字"
                  />
                  <Button type="primary" icon={<SendOutlined />} loading={saving} disabled={!reply.trim()} onClick={sendReply}>发送回复</Button>
                </div>
              </div>
            ) : <Alert className="ticket-readonly-alert" type="info" showIcon message="该反馈已关闭，当前为只读状态" />}
          </section>
        </>
      ) : null}
    </div>
  );
};

export default TicketDetail;
